import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RoomsManager = ({ rooms, onClose, onSave }) => {
  const [newRoomName, setNewRoomName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAddRoom = async (e) => {
    e.preventDefault();
    if (!newRoomName.trim()) return;

    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      await axios.post(
        `${API}/rooms`,
        { name: newRoomName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Sala dodana pomyślnie');
      setNewRoomName('');
      onSave();
    } catch (error) {
      toast.error('Błąd podczas dodawania sali');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRoom = async (roomId, roomName) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć salę "${roomName}"?`)) return;

    const token = localStorage.getItem('token');

    try {
      await axios.delete(`${API}/rooms/${roomId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Sala usunięta pomyślnie');
      onSave();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Nie można usunąć sali z zaplanowanymi imprezami');
      } else {
        toast.error('Błąd podczas usuwania sali');
      }
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px]" data-testid="rooms-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Zarządzanie salami
          </DialogTitle>
          <DialogDescription>
            Dodaj lub usuń sale do wyświetlania na ekranach
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          {/* Add new room form */}
          <form onSubmit={handleAddRoom} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="room_name">Nazwa nowej sali</Label>
              <div className="flex gap-2">
                <Input
                  id="room_name"
                  value={newRoomName}
                  onChange={(e) => setNewRoomName(e.target.value)}
                  placeholder="np. Sala 3"
                  className="h-11"
                  data-testid="new-room-input"
                />
                <Button
                  type="submit"
                  className="h-11 text-white"
                  style={{ background: '#6E35C0' }}
                  disabled={loading || !newRoomName.trim()}
                  data-testid="add-room-button"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </form>

          {/* Existing rooms list */}
          <div className="space-y-2">
            <Label>Istniejące sale</Label>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {rooms.length === 0 ? (
                <p className="text-center py-4 text-gray-500">Brak sal</p>
              ) : (
                rooms.map((room) => (
                  <div
                    key={room.id}
                    className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200"
                    data-testid={`room-item-${room.id}`}
                  >
                    <span className="font-semibold">{room.name}</span>
                    <Button
                      onClick={() => handleDeleteRoom(room.id, room.name)}
                      variant="ghost"
                      size="sm"
                      className="hover:bg-red-100 hover:text-red-600"
                      data-testid={`delete-room-${room.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 h-11"
              data-testid="close-rooms-button"
            >
              Zamknij
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RoomsManager;
